import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Compass, Users, Wrench } from "lucide-react";
import Seo from "@/components/Seo";
import { useLang } from "@/i18n/LanguageContext";
import { BUSINESS, IMAGES, STATS, pick } from "@/data/site";

const VALUES = [
  { id: "quality", icon: ShieldCheck, chip: "bg-orange-100 text-orange-600" },
  { id: "engineering", icon: Compass, chip: "bg-teal-100 text-teal-600" },
  { id: "transparency", icon: Users, chip: "bg-amber-100 text-amber-600" },
  { id: "equipment", icon: Wrench, chip: "bg-purple-100 text-purple-600" },
];

const STAT_COLORS = ["text-orange-600 border-orange-500", "text-teal-600 border-teal-500", "text-amber-600 border-amber-500", "text-blue-600 border-blue-500"];

export default function About() {
  const { t, lang } = useLang();
  return (
    <div data-testid="about-page">
      <Seo
        title="About Us — V.B. Sharada Associates | Engineering & Construction Firm, Tandur"
        description="Learn about V.B. Sharada Associates — a Tandur-based contracting, planning, construction, interiors and engineering services firm with 15+ years of experience."
      />

      <section className="warm-grid border-b border-orange-100">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-14 md:py-20">
          <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 font-semibold text-xs uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            {t("abt.eyebrow")}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 max-w-3xl">
            {t("abt.h1")}
          </h1>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-full h-full rounded-3xl bg-gradient-to-br from-orange-200 via-amber-100 to-teal-100" />
            <img
              src={IMAGES.site}
              alt="V.B. Sharada Associates construction site in progress in Telangana"
              className="relative rounded-3xl shadow-lg w-full h-80 sm:h-96 object-cover"
            />
          </div>
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{t("abt.whoH2")}</h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">{t("abt.p1")}</p>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">{t("abt.p2")}</p>
            <div className="mt-8 grid grid-cols-2 gap-6">
              {STATS.slice(0, 4).map((s, i) => (
                <div key={s.label} className={`border-l-4 pl-4 ${STAT_COLORS[i % STAT_COLORS.length].split(" ")[1]}`}>
                  <div className={`font-heading text-2xl font-extrabold ${STAT_COLORS[i % STAT_COLORS.length].split(" ")[0]}`}>{s.value}</div>
                  <div className="text-xs text-slate-500 mt-1">{pick(s, "label", lang)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white border-y border-orange-100">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 font-semibold text-xs uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
              {t("abt.standardsEyebrow")}
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              {t("abt.whyH2")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v) => (
              <div
                key={v.id}
                data-testid={`value-${v.id}`}
                className="bg-orange-50/40 border border-orange-100 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div className={`w-12 h-12 rounded-2xl ${v.chip} flex items-center justify-center mb-5`}>
                  <v.icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{t(`values.${v.id}.title`)}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{t(`values.${v.id}.text`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="px-4 sm:px-8 lg:px-12 max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 font-semibold text-xs uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
              {t("abt.leadEyebrow")}
            </div>
            <blockquote className="font-heading text-xl sm:text-2xl font-semibold text-slate-900 leading-snug border-l-4 border-orange-500 pl-5">
              "{t("abt.quote")}"
            </blockquote>
            <div className="mt-5 text-sm text-slate-500">{t("abt.leadBy")}</div>
          </div>
          <div className="lg:col-span-2 bg-slate-900 rounded-3xl p-7 text-white blueprint-grid-dark relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full bg-teal-500/20 blur-2xl" />
            <h3 className="font-heading text-lg font-semibold mb-3">{t("abt.officeH3")}</h3>
            <p className="text-sm text-slate-300 leading-relaxed">{BUSINESS.address}</p>
            <p className="mt-3 text-sm text-slate-300">{pick(BUSINESS, "hours", lang)}</p>
            <Link
              to="/contact"
              data-testid="about-contact-button"
              className="mt-5 inline-flex items-center gap-2 bg-orange-500 text-white font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-orange-600 transition-colors duration-200 shadow-md shadow-orange-500/30"
            >
              {t("abt.contactBtn")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
